// @ts-nocheck
import { OptionDependencyData, DefaultStepOptionState, MeasurementDependencyDimensionValue } from "./types";

export const escapeHtml = (unsafe) => {
  return unsafe.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;').replaceAll("'", '&#039;');
}

// Use this if the elements you need to query are nested more than 1 level deep inside a slot
export const bindAssignedElements = (slotElement:HTMLSlotElement) => {
  // Step 1 - get the assigned elements from the slot
  const assignedElements = slotElement?.assignedElements();

  // Step two - 
  // The function takes a string and checks all the assigned 
  // elements to see if it matches the selector
  // This will return either the assigned element or any of it's matching children
  return <E>(selector:string):E|null => assignedElements.reduce((matches, currentEl) => {

    if (currentEl.tagName.toLowerCase() === selector) return [ ...matches, currentEl ];

    const possibleMatchingElements = currentEl.querySelectorAll(selector);
    if (possibleMatchingElements.length) {
      const matchingEls = possibleMatchingElements.length > 1 ? possibleMatchingElements : possibleMatchingElements[0];
      return [ ...matches, matchingEls ]
    }

    return matches;

  }, []).flat();
}

export const generateDependencies = (dependency:Option) => {
  const dependencyValues = [
    'dependency_option_ids',
    'disable_on_dependency_selected',
    'hide_on_dependency_selected',
    'has_measurement_dependency',
    'measurement_dependency_dimension',
    'dependency_rule_connection',
    'measurement_dependency_lower_value',
    'measurement_dependency_upper_value'
  ].reduce((acc, key) => {
    return {
      ...acc,
      [key]: dependency[key]
    }
  }, {});

  const stateOnDependencySelection:DefaultStepOptionState = (() => {
    if (dependencyValues.hide_on_dependency_selected) return 'Hidden';
    else if (dependencyValues.disable_on_dependency_selected) return 'Disabled';
    else return 'Visible';
  })();

  // EG: ID1 or ID2 and ID10
  // [['ID1', 'ID2'], ['ID10']] 
  // Any IDs from one array and any ids from the other must be selected
  const idGroups = dependencyValues.dependency_option_ids.length ? dependencyValues.dependency_option_ids
    .split('and')
    .map(a_group => {
      return a_group.split('or')
        .map(optionId => optionId.trim());
    }) : null;
  
  const measurementDependency = dependencyValues.has_measurement_dependency ? {
    dimension: dependencyValues.measurement_dependency_dimension,
    values: {
      lower: parseInt(dependencyValues.measurement_dependency_lower_value),
      upper: parseInt(dependencyValues.measurement_dependency_upper_value),
    }
  } : null;

  if (!idGroups && !measurementDependency) return null;

  return {
    measurementDependency,
    idGroups,
    action: stateOnDependencySelection,
    rule_connection: dependencyValues.dependency_rule_connection,
  }
}
export const modifyPrice = (price, discount) => {
  /* IMPORTANT */
	// This function is used in the src/api/shopify.js file in the CBCo-JAM-WORKER codebase
  // If it is changed here, it should probably be changed there.

  const discountValue = parseInt(discount ?? 0);

  if (discountValue <= 0) return price;
  const discountIsPercentage = discount.includes('%');

  if (discountIsPercentage) {
    const percentValue = parseInt(discount.replace('%', '')) / 100;

    return price * (1 - percentValue);
  } else {
    return price - discountValue;
  }
}
export const generateStepId = stepTitle => stepTitle.toUpperCase().replace(/[AEIOU ]/gi, '').substr(0, 6);
export const valueIsInRange = (valueToCheck:number, lowerBound:number, upperBound:number) => {
  return valueToCheck >= lowerBound && valueToCheck <= upperBound;
}
export class StateDependant extends HTMLElement {
  protected default_state:DefaultStepOptionState = 'Visible';
  protected current_state:DefaultStepOptionState = 'Visible';
  public dependencies:Dependency;
  constructor() {
    super();
  }

  public get isVisible() {
    return this.current_state === 'Visible';
  }

  public get isDisabled() {
    return this.current_state === 'Disabled';
  }

  get stateClasses() {
    return {
      // Visible: 'is-visible', // Not needed. Options are visible by default
      Hidden: 'is-hidden',
      Disabled: 'is-disabled'
    };
  }

  public setStepId(id:string) {
    // This function is implemented in multiple spots.
    // If changing it here, update the otehr locations
    this.step_id = id;
    const componenentsWithStepId = this.querySelectorAll('[step-id]');
    componenentsWithStepId.forEach(component => component?.setStepId(id))
  }

  get allStates() {
    return Object.keys(this.stateClasses);
  }

  setState(state:DefaultStepOptionState) {
    if (this.current_state === state) return;
    // remove any previous state classes
    this.allStates.forEach(state => {
      const stateClass = this.stateClasses[state];
      this.classList.remove(stateClass);
    });
    this.current_state = state;

    if (this.stateClasses[state]) this.classList.add(this.stateClasses[state]);
  }
}